import { CheckCircleOutlineRounded, HighlightOffRounded, SendRounded } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import {getProduct, RESPONSE_CHECK_CODE, useCouponQuery} from "../../../../../api/services";
import { FormTextField } from "../../../../../components/fields";
import { ReactQueryKey } from "../../../../../types";
import { isBusinessContinuityMode, isLeisureCode, stepState } from "../../../recoil";
import { IFormValues, InsertBookingStep } from "../../../types";
import { getCarGroupCode } from "../../../../../utils";

export const FormFcaBankCardNumber = () => {

    const { t } = useTranslation('booking');

    const step = useRecoilValue(stepState);
    const businessContinuityMode = useRecoilValue(isBusinessContinuityMode);
    const flagLeisureCode = useRecoilValue(isLeisureCode);

    const formContext = useFormContext<IFormValues>();

    const fcaBankCardNumber = useWatch<IFormValues, 'extraServices.fcaBankCardNumber'>({
        name: 'extraServices.fcaBankCardNumber'
    });
    const pickupLocation = useWatch<IFormValues, 'basicDetails.pickupLocation'>({
        name: 'basicDetails.pickupLocation'
    });
    const dropoffLocation = useWatch<IFormValues, 'basicDetails.dropoffLocation'>({
        name: 'basicDetails.dropoffLocation'
    });
    const pickupDate = useWatch<IFormValues, 'basicDetails.pickupDate'>({
        name: 'basicDetails.pickupDate'
    });

    const dropoffDate = useWatch<IFormValues, 'basicDetails.dropoffDate'>({
        name: 'basicDetails.dropoffDate'
    });

    const carGroup = useWatch<IFormValues, 'basicDetails.carGroup'>({
        name: 'basicDetails.carGroup'
    });

    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });
    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const productsQuery = useQuery({
        queryKey: [ReactQueryKey.Product, {
            segment: rentalType,
            tenant: tenant,
            dealer: dealer
        }],
        queryFn: () => getProduct({
            segment: rentalType,
            businessContinuityInsToolMode: businessContinuityMode,
            tenant: tenant as string,
            dealer: dealer as string
        }),
        enabled: false
    });

    const queryClient = useQueryClient();

    const couponQuery = useCouponQuery(
        {
            pickUpDate: pickupDate?.isValid() ? pickupDate.toISOString() : undefined,
            dropoffDate: dropoffDate?.isValid() ? dropoffDate.toISOString() : undefined,
            pickUpLocation: pickupLocation?.id,
            dropOffLocation: dropoffLocation?.id,
            promoCode: fcaBankCardNumber,
            carGroupCode: !!carGroup ? getCarGroupCode(carGroup) : undefined,
            productCodes: !!productsQuery?.data?.products ? productsQuery.data.products.map(elem => {return elem.code}) : undefined,
            tenant: tenant as string,
            dealer: dealer as string
        },
        {
            enabled: false,
            onSuccess: (data) => {
                if (data?.code !== RESPONSE_CHECK_CODE.SUCCESS) {
                    formContext.setError('extraServices.fcaBankCardNumber', {
                        message: 'message.validation.invalid_code'
                    });
                    return;
                }
                formContext.clearErrors('extraServices.fcaBankCardNumber');
            }
        }
    );

    const handleCheck = useCallback(() => {
        couponQuery.refetch();
    }, []);
    const handleKeyDown = useCallback(() => {
        queryClient.resetQueries([ReactQueryKey.Coupon, {
            tenant: formContext.getValues('basicDetails.tenants') as string,
            dealer: formContext.getValues('basicDetails.dealers') as string
        }]);
        if (formContext.getFieldState('extraServices.fcaBankCardNumber').error) {
            formContext.clearErrors('extraServices.fcaBankCardNumber');
        }
    }, []);

    const EndAdornemnt = useMemo(() => {
        if (
            !fcaBankCardNumber ||
            !pickupDate ||
            !dropoffDate ||
            !pickupLocation ||
            !dropoffLocation ||
            !carGroup||
            !productsQuery?.data?.products
        ) {
            return undefined;
        }
        if (couponQuery.isFetching) {
            return <CircularProgress size={20} />
        }
        if (!couponQuery.data) {
            return (
                <InputAdornment position="end">
                    <IconButton
                        onClick={handleCheck}
                        disabled={step === InsertBookingStep.CUSTOMER_DETAILS}
                    >
                        <SendRounded />
                    </IconButton>
                </InputAdornment>
            )
        }
        if (couponQuery.data.code !== RESPONSE_CHECK_CODE.SUCCESS) {
            return (
                <HighlightOffRounded
                    color="error"
                />
            );
        }
        return (
            <CheckCircleOutlineRounded
                color="success"
            />
        );
    }, [
        fcaBankCardNumber,
        pickupDate,
        dropoffDate,
        pickupLocation,
        dropoffLocation,
        carGroup,
        couponQuery,
        productsQuery?.data?.products,
        step
    ]);

    return (
        <FormTextField<IFormValues>
            name="extraServices.fcaBankCardNumber"
            label={t('label.fcaBankCardNumber') + '*'}
            disabled={couponQuery.isFetching || step === InsertBookingStep.CUSTOMER_DETAILS}
            InputProps={{
                endAdornment: EndAdornemnt
            }}
            onKeyDown={handleKeyDown}
        />
    );
};

export default FormFcaBankCardNumber;
